import { Settings } from '@lightningjs/sdk'

import { FirebaseApp, initializeApp } from 'firebase/app'
import { Database, getDatabase, ref } from 'firebase/database'
import {
  collection,
  Firestore,
  getFirestore,
  limit,
  orderBy,
  query,
  QuerySnapshot,
  where,
} from 'firebase/firestore'
import { SportData, StreamItem } from '../types/flo-experience'
import { isStagingEnabled } from './settingsUtils'

const getLiveConfig = () => {
  const apiKey = Settings.get(
    'app',
    isStagingEnabled() ? 'env.APP_FIREBASE_STAGING_API_KEY' : 'env.APP_FIREBASE_API_KEY',
    '',
  )
  const appId = Settings.get(
    'app',
    isStagingEnabled() ? 'env.APP_FIREBASE_STAGING_APP_ID' : 'env.APP_FIREBASE_APP_ID',
    '',
  )
  const databaseURL = Settings.get(
    'app',
    isStagingEnabled() ? 'env.APP_FIREBASE_STAGING_DB_URL' : 'env.APP_FIREBASE_DB_URL',
    '',
  )
  const projectId = Settings.get(
    'app',
    isStagingEnabled() ? 'env.APP_FIREBASE_STAGING_PROJECT_ID' : 'env.APP_FIREBASE_PROJECT_ID',
    '',
  )

  return {
    apiKey,
    appId,
    databaseURL,
    projectId,
  }
}

const getSportDataConfig = () => {
  const apiKey = Settings.get(
    'app',
    isStagingEnabled()
      ? 'env.APP_STAGING_FIREBASE_SPORT_DATA_API_KEY'
      : 'env.APP_FIREBASE_SPORT_DATA_API_KEY',
    '',
  )
  const appId = Settings.get(
    'app',
    isStagingEnabled()
      ? 'env.APP_STAGING_FIREBASE_SPORT_DATA_APP_ID'
      : 'env.APP_FIREBASE_SPORT_DATA_APP_ID',
    '',
  )
  const databaseURL = Settings.get(
    'app',
    isStagingEnabled()
      ? 'env.APP_STAGING_FIREBASE_SPORT_DATA_DB_URL'
      : 'env.APP_FIREBASE_SPORT_DATA_DB_URL',
    '',
  )
  const projectId = Settings.get(
    'app',
    isStagingEnabled()
      ? 'env.APP_STAGING_FIREBASE_SPORT_DATA_PROJECT_ID'
      : 'env.APP_FIREBASE_SPORT_DATA_PROJECT_ID',
    '',
  )

  return {
    apiKey,
    appId,
    databaseURL,
    projectId,
  }
}

export const streamIdsArr = (streams = []) => {
  return streams.map((item: StreamItem) => item.id)
}

export const streamsArr = (streams: Record<string, object> = {}) => {
  return Object.entries(streams)
    .map(([key, value]) => ({
      id: parseInt(key, 10),
      ...value,
    }))
    .filter((item: StreamItem) => item.isActive === true) as StreamItem[]
}

export const areStreamsEqual = (previous, latest) => {
  const previousStreams = streamIdsArr(previous)
  const latestStreams = streamIdsArr(latest)

  return (
    JSON.stringify(previousStreams.sort((a, b) => a - b)) ===
    JSON.stringify(latestStreams.sort((a, b) => a - b))
  )
}

export const getLatestDocs = (snapshot: QuerySnapshot) => {
  const data = snapshot.docs.map((item: any) => item.data()) as SportData[]

  const mostRecentById = new Map()

  data.forEach((item) => {
    const current = mostRecentById.get(item.identity.liveStreamId)
    if (!current || item.lastUpdated > current.lastUpdated) {
      mostRecentById.set(item.identity.liveStreamId, item)
    }
  })

  const result = Array.from(mostRecentById.values())

  return result
}

export class Firebase {
  protected _liveConfig = getLiveConfig()
  protected _sportDataConfig = getSportDataConfig()

  protected _primaryApp: FirebaseApp
  protected _secondaryApp: FirebaseApp

  protected _realTimeDb: Database
  protected _firestore: Firestore

  get db() {
    return this._realTimeDb
  }

  get firestore() {
    return this._firestore
  }

  public init() {
    try {
      this._primaryApp = initializeApp(this._liveConfig, 'primary')
      this._realTimeDb = getDatabase(this._primaryApp)

      this._secondaryApp = initializeApp(this._sportDataConfig, 'secondary')
      this._firestore = getFirestore(this._secondaryApp)
    } catch (error) {
      console.error('Error during Firebase init', error)
    }
  }

  public query(streamIds, liveId) {
    return query(
      collection(this.firestore, 'summary'),
      where('statusCode', '>=', 20),
      where('statusCode', '<=', 100),
      where('identity.liveEventId', '==', liveId),
      where('identity.liveStreamId', 'in', streamIds),
      orderBy('statusCode', 'asc'),
      orderBy('lastUpdated', 'desc'),
      limit(100),
    )
  }

  public eventDbRef(liveId) {
    return ref(this.db, `events/${liveId}`)
  }
}
